import { zodResolver } from "@hookform/resolvers/zod";
import useToast from "@repo/core/components/core/Toast";
import { proposalInfoSecurityFormDataSchema } from "@repo/core/helpers/zod.schemas";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { apiInstance } from "@repo/core/services/instances/api";
import { wayupApiInstance } from "@repo/core/services/instances/wayup-api";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import { CommonResource } from "@repo/core/services/resources/common";
import { InfoSecurityFormDataSchemaType } from "@repo/core/stores/proposalForm/types";
import { useForm } from "react-hook-form";
import { usePreProposal } from "../../hooks/usePreProposal";
import { usePreProposalStore } from "../../store/preProposalStore";
import type { PreProposalData } from "../../store/preProposalStore";
import { cancelPreProposalAlterarSyncDebounce } from "../../services/preProposalAlterarSyncDebounce.service";
import { ROUTES } from "@repo/core/routes";
import { ClientResource } from "@repo/core/services/resources/client";
import {
  useProposalValidation,
  ProposalValidationAction,
  ProposalFlowType,
  ProposalValidationResult,
  resolveProposalStatusValidation
} from "@repo/core/hooks/useProposalValidation";
import { useRef, useEffect, useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { IActionsheetRefProps } from "@repo/core/components/layout/Actionsheet/types";
import { isValidCPF } from "@repo/core/helpers/zod.helpers";
import { getCleanCPF, sanitizeNameInput } from "@repo/core/utils/string.utils";
import {
  extractInfoSecurityFormData,
  mapProposalToProposalDataPreservingFlowContext,
  mapDraftToFormData,
  mapClientInfoToReanalysisProposalInput,
  mapClientInfoToProposalData,
  mapToContextUpdateInput,
  mapClientAdditionalPhoneToProposalData,
  mapWayupProposalPhoneTypeToProposalData
} from "./mappers";
import {
  ConsultProposalResponseDTO,
  PROPOSTA_SITUACAO
} from "@repo/core/services/entities/Proposals";
import type { ProposalPhoneTokenStatusResponseDTO } from "@repo/core/services/entities/WayupRequisitions";
import { LimitReassessmentEnum } from "@repo/core/enums/proposals";
import {
  isReanalysisProposal,
  shouldConfirmTokenWhenKeepingCurrentPhone
} from "../BiometryValidation/services/navigation.service";
import { useResolveRequiredDocumentsAfterSecurity } from "../../hooks/useResolveRequiredDocumentsAfterSecurity";
import {
  forceNewBiometryPatch,
  resolveNeedNewBiometryPatch
} from "../../services/proposalEditBiometry.service";
import {
  INFO_SECURITY_FEEDBACK_TOASTS,
  resolveInfoSecurityProcessingState,
  shouldNotifyProposalLoaded
} from "./feedback";

const proposalsResource = new ProposalsResource(apiInstance);
const clientResource = new ClientResource(apiInstance);
const commonResource = new CommonResource(apiInstance);
const requisitionsResource = new RequisitionsResource(wayupApiInstance);

const CPF_VALIDATION_TIMEOUT_MS = 45_000;
const CPF_VALIDATION_TIMEOUT_ERROR = "CPF_VALIDATION_TIMEOUT";

const pickFirstTrimmedString = (
  ...values: Array<string | undefined | null>
): string => {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return "";
};

const fetchProfileNameFallback = async (
  cleanCPF: string,
  cachedFirstCardInfo?: ProposalValidationResult["firstCardInfo"]
): Promise<{
  firstCardInfo?: ProposalValidationResult["firstCardInfo"];
  fullNameFromProfile: string;
}> => {
  const cachedName = pickFirstTrimmedString(cachedFirstCardInfo?.strNome);

  if (cachedName) {
    return {
      firstCardInfo: cachedFirstCardInfo,
      fullNameFromProfile: cachedName
    };
  }

  try {
    const { data } = await clientResource.consultProfileProposals(cleanCPF);
    const firstCardInfo = data?.lstCartoes?.[0] || cachedFirstCardInfo;

    return {
      firstCardInfo,
      fullNameFromProfile: pickFirstTrimmedString(
        firstCardInfo?.strNome,
        (data as { strNome?: string } | undefined)?.strNome
      )
    };
  } catch {
    return {
      firstCardInfo: cachedFirstCardInfo,
      fullNameFromProfile: ""
    };
  }
};

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  timeoutMessage: string
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(timeoutMessage)), ms)
    )
  ]);
}

const shouldAskToKeepCurrentPhone = (
  proposal?: ConsultProposalResponseDTO
): boolean => {
  if (!proposal) {
    return false;
  }

  const isDraftStatus =
    proposal.strSituacao === PROPOSTA_SITUACAO.DOCUMENT_CONFERENCE ||
    proposal.strSituacao === PROPOSTA_SITUACAO.DATA_CONFERENCE;

  return (
    isDraftStatus &&
    !!proposal.strCelularDDD?.trim() &&
    !!proposal.strCelular?.trim()
  );
};

const getProposalPhone = (
  proposal?: Pick<PreProposalData, "strEndDdd1" | "strEndFone1">
): { phoneDDD: string; phone: string } => {
  return {
    phoneDDD: proposal?.strEndDdd1?.trim() || "",
    phone: proposal?.strEndFone1?.trim() || ""
  };
};

const hasCompleteProposalPhone = (
  proposal?: Pick<PreProposalData, "strEndDdd1" | "strEndFone1">
): boolean => {
  const { phoneDDD, phone } = getProposalPhone(proposal);
  return !!phoneDDD && !!phone;
};

const isSavedCPF = (
  cleanCPF: string,
  proposal?: Pick<PreProposalData, "strCliente">
): boolean => {
  const savedCleanCPF = getCleanCPF(proposal?.strCliente || "");
  return !!savedCleanCPF && savedCleanCPF === cleanCPF;
};

const shouldAskToKeepCurrentProposalPhone = (
  proposal?: Pick<PreProposalData, "strSituacao" | "strEndDdd1" | "strEndFone1">
): boolean => {
  if (!proposal) {
    return false;
  }

  const isDraftStatus =
    proposal.strSituacao === PROPOSTA_SITUACAO.DOCUMENT_CONFERENCE ||
    proposal.strSituacao === PROPOSTA_SITUACAO.DATA_CONFERENCE;

  return isDraftStatus && hasCompleteProposalPhone(proposal);
};

export const InfoSecurityController = () => {
  const { back, push, replace } = useNavigation();
  const { success, error } = useToast();

  const { setProposalData, proposalData } = usePreProposal();
  const { resolveRequiredDocumentsAfterSecurity } =
    useResolveRequiredDocumentsAfterSecurity();
  const { validateProposals } = useProposalValidation();
  const reanalysisActionSheetRef = useRef<IActionsheetRefProps>(null);
  const phoneUpdateActionSheetRef = useRef<IActionsheetRefProps>(null);
  const reanalysisProposalIdRef = useRef<string | null>(null);

  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { isValid, isSubmitting },
    handleSubmit
  } = useForm<InfoSecurityFormDataSchemaType>({
    resolver: zodResolver(proposalInfoSecurityFormDataSchema)
  });

  const cpfValue = watch("cpf");
  const hasLoadedDraft = useRef(false);
  const hasConfirmedReanalysis = useRef(false);
  const hasBlockedAction = useRef(false);

  // Ref that stays in sync with the proposal reassessment field to avoid stale closures
  const hasLimitReassessmentRef = useRef(isReanalysisProposal(proposalData));
  hasLimitReassessmentRef.current = isReanalysisProposal(proposalData);
  const queueEditModeRef = useRef(proposalData?.queueEditMode);
  queueEditModeRef.current = proposalData?.queueEditMode;
  const selectedProductCode = proposalData?.strProduto?.trim() || "";
  const selectedProductCodeRef = useRef(selectedProductCode);
  selectedProductCodeRef.current = selectedProductCode;
  const blockActionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [isValidatingCPF, setIsValidatingCPF] = useState(false);
  const [isCreatingReanalysis, setIsCreatingReanalysis] = useState(false);
  const [isCpfLocked, setIsCpfLocked] = useState(false);
  const [isSavingInfoSecurity, setIsSavingInfoSecurity] = useState(false);
  const [isContinuePressed, setIsContinuePressed] = useState(false);
  const [isKeepingCurrentPhone, setIsKeepingCurrentPhone] = useState(false);

  useQuery({
    queryKey: ["proposalPhoneTokenStatus", proposalData?.lngProposta],
    queryFn: async () => {
      const { data } = await requisitionsResource.getProposalPhoneTokenStatus(
        proposalData?.lngProposta || ""
      );
      return data;
    },
    enabled: !!proposalData?.lngProposta,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  /** Last successful `validateProposals` for a CPF (debounced blur). Reused on submit to avoid duplicate ConsultarPerfilPropostas. */
  const lastProfileValidationRef = useRef<{
    cleanCPF: string;
    productCode: string;
    result: ProposalValidationResult;
  } | null>(null);

  /** Bumped when CPF validation is invalidated, so stale async completions do not clear loading incorrectly. */
  const cpfValidationSeqRef = useRef(0);

  const handleBlockAction = (message?: string) => {
    if (hasBlockedAction.current) {
      return;
    }

    hasBlockedAction.current = true;

    if (message) {
      error(message);
    }

    if (blockActionTimeoutRef.current) {
      clearTimeout(blockActionTimeoutRef.current);
    }

    blockActionTimeoutRef.current = setTimeout(() => {
      replace(ROUTES.home);
    }, 800);
  };

  const handleReanalysisAction = () => {
    if (hasLimitReassessmentRef.current || hasConfirmedReanalysis.current) {
      return;
    }
    reanalysisActionSheetRef.current?.open();
  };

  const openPhoneUpdateOrGoToPhoneNumber = useCallback(() => {
    const latestProposalData = usePreProposalStore.getState().proposalData;

    if (hasCompleteProposalPhone(latestProposalData)) {
      phoneUpdateActionSheetRef.current?.open();
      return;
    }

    push(ROUTES.preProposalsPhoneNumber);
  }, [push]);

  const handleLoadDraftAction = (
    proposal: ConsultProposalResponseDTO,
    cleanCPF: string,
    message?: string,
    hasLimitReassessmentDraft = false
  ) => {
    hasLoadedDraft.current = true;
    setIsCpfLocked(true);

    const proposalDataMapped = mapProposalToProposalDataPreservingFlowContext(
      proposal,
      hasLimitReassessmentDraft
        ? {
            ...proposalData,
            strReavaliacaoLimite: LimitReassessmentEnum.REANALYSIS
          }
        : proposalData
    );

    setProposalData(
      {
        ...proposalDataMapped,
        ...(hasLimitReassessmentDraft && {
          strReavaliacaoLimite: LimitReassessmentEnum.REANALYSIS
        })
      },
      { skipAlterarSync: true }
    );

    const draftData = mapDraftToFormData(proposal, cleanCPF);

    setValue("cpf", draftData.cleanCPF);
    setValue("fullName", sanitizeNameInput(draftData.fullName));

    if (message) {
      error(message);
    }

    success(
      INFO_SECURITY_FEEDBACK_TOASTS.proposalLoaded.title,
      INFO_SECURITY_FEEDBACK_TOASTS.proposalLoaded.message
    );
  };

  const handleCPFChange = useCallback(
    async (cpf: string) => {
      // Skip validation entirely if reanalysis was already confirmed or in progress
      if (
        hasLimitReassessmentRef.current ||
        hasConfirmedReanalysis.current ||
        queueEditModeRef.current
      ) {
        setIsValidatingCPF(false);
        return;
      }

      if (hasLoadedDraft.current) {
        setIsValidatingCPF(false);
        return;
      }

      const cleanCPF = getCleanCPF(cpf);

      if (!isValidCPF(cleanCPF)) {
        lastProfileValidationRef.current = null;
        setIsValidatingCPF(false);
        return;
      }

      const cachedValidation = lastProfileValidationRef.current;
      if (
        cachedValidation &&
        cachedValidation.cleanCPF === cleanCPF &&
        cachedValidation.productCode === selectedProductCodeRef.current &&
        !cachedValidation.result.error
      ) {
        setIsValidatingCPF(false);
        return;
      }

      setIsValidatingCPF(true);
      const seqAtStart = cpfValidationSeqRef.current;

      try {
        const validationResult = await withTimeout(
          validateProposals(cleanCPF, selectedProductCodeRef.current),
          CPF_VALIDATION_TIMEOUT_MS,
          CPF_VALIDATION_TIMEOUT_ERROR
        );

        // Re-check after async call in case reanalysis was confirmed while waiting
        if (
          hasLimitReassessmentRef.current ||
          hasConfirmedReanalysis.current ||
          queueEditModeRef.current
        ) {
          return;
        }

        if (seqAtStart !== cpfValidationSeqRef.current) {
          return;
        }

        if (validationResult.error) {
          lastProfileValidationRef.current = null;
          return;
        }

        lastProfileValidationRef.current = {
          cleanCPF,
          productCode: selectedProductCodeRef.current,
          result: validationResult
        };

        switch (validationResult.action) {
          case ProposalValidationAction.BLOCK:
            handleBlockAction(validationResult.message);
            break;

          case ProposalValidationAction.REANALYSIS:
            reanalysisProposalIdRef.current =
              validationResult.proposalId || null;
            if (validationResult.clientName) {
              setValue(
                "fullName",
                sanitizeNameInput(validationResult.clientName)
              );
            }
            handleReanalysisAction();
            break;

          case ProposalValidationAction.LOAD_DRAFT:
            if (validationResult.proposalDetails) {
              handleLoadDraftAction(
                validationResult.proposalDetails,
                cleanCPF,
                validationResult.message,
                validationResult.flowType === ProposalFlowType.REANALYSIS
              );
            }
            break;
        }
      } catch (err) {
        lastProfileValidationRef.current = null;
        if (
          err instanceof Error &&
          err.message === CPF_VALIDATION_TIMEOUT_ERROR
        ) {
          error(
            "Tempo esgotado ao validar o CPF. Verifique sua conexão e tente novamente."
          );
        } else {
          console.error("Error validating CPF:", err);
        }
      } finally {
        if (seqAtStart === cpfValidationSeqRef.current) {
          setIsValidatingCPF(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const invalidatePendingCpfValidation = useCallback(() => {
    cpfValidationSeqRef.current += 1;
    setIsValidatingCPF(false);
  }, []);

  useEffect(() => {
    invalidatePendingCpfValidation();

    if (!cpfValue) {
      lastProfileValidationRef.current = null;
      return;
    }

    const cleanCPF = getCleanCPF(cpfValue);
    const cachedValidation = lastProfileValidationRef.current;

    if (
      cachedValidation &&
      (cachedValidation.cleanCPF !== cleanCPF ||
        cachedValidation.productCode !== selectedProductCode)
    ) {
      lastProfileValidationRef.current = null;
    }
  }, [cpfValue, invalidatePendingCpfValidation, selectedProductCode]);

  useEffect(() => {
    return () => {
      if (blockActionTimeoutRef.current) {
        clearTimeout(blockActionTimeoutRef.current);
      }
    };
  }, []);

  // Reset hasLoadedDraft when CPF changes from the saved proposal CPF
  useEffect(() => {
    if (!cpfValue || !proposalData?.strCliente) return;

    const currentCleanCPF = getCleanCPF(cpfValue);
    const savedCleanCPF = getCleanCPF(proposalData.strCliente);

    if (currentCleanCPF !== savedCleanCPF && hasLoadedDraft.current) {
      hasLoadedDraft.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpfValue]);

  useEffect(() => {
    if (proposalData?.strNome && !hasLoadedDraft.current) {
      setValue("fullName", sanitizeNameInput(proposalData.strNome));
    }
    if (proposalData?.strCliente && !hasLoadedDraft.current) {
      setValue("cpf", proposalData.strCliente);
      hasLoadedDraft.current = true;
      setIsCpfLocked(true);
    }

    // When reanalysis was already confirmed, mark draft as loaded to prevent
    // CPF validation from triggering and reopening the reanalysis modal,
    // and lock CPF field (cannot edit after reanalysis)
    if (isReanalysisProposal(proposalData) || proposalData?.queueEditMode) {
      hasLoadedDraft.current = true;
      setIsCpfLocked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmReanalysis = async () => {
    if (!cpfValue) return;

    const proposalId = reanalysisProposalIdRef.current;

    // Reanalysis cannot proceed without the previous proposal, otherwise fields
    // such as MAE/SEXO/NASCIMENTO/NRODOCIDENT/ATIVSETOR/DIACARTAO/ENVIOFATURA
    // would be silently dropped from /Proposta/Incluir.
    if (!proposalId) {
      error("Não foi possível recuperar a proposta anterior. Tente novamente.");
      return;
    }

    hasConfirmedReanalysis.current = true;
    // Cancel any pending CPF validation to prevent modal from reopening
    invalidatePendingCpfValidation();
    setIsCpfLocked(true);
    setIsCreatingReanalysis(true);

    const cleanCPF = getCleanCPF(cpfValue);

    const { fullName: fullNameFromForm } = extractInfoSecurityFormData({
      cpf: getValues("cpf") || "",
      fullName: getValues("fullName") || ""
    });
    // Reuse the first card cached by validateProposals (CPF blur), so the name
    // from /ConsultarPerfilPropostas is not lost when /ConsultarProposta omits it.
    let firstCardInfo =
      lastProfileValidationRef.current?.cleanCPF === cleanCPF &&
      lastProfileValidationRef.current.productCode === selectedProductCode
        ? lastProfileValidationRef.current.result.firstCardInfo
        : undefined;
    let fullNameFromProfile = pickFirstTrimmedString(firstCardInfo?.strNome);

    try {
      const [{ data: proposalDetails }, wayupProposalDataResponse] =
        await Promise.all([
          proposalsResource.getProposalDetails(proposalId),
          requisitionsResource.getProposalData(proposalId).catch((err) => {
            console.error(
              "Error loading Wayup API proposal data for reanalysis:",
              err
            );
            return undefined;
          })
        ]);
      const wayupProposalPhoneType = mapWayupProposalPhoneTypeToProposalData(
        wayupProposalDataResponse?.data || {}
      );

      const statusValidation = resolveProposalStatusValidation(
        proposalDetails.strSituacao
      );

      if (statusValidation.action === ProposalValidationAction.BLOCK) {
        hasConfirmedReanalysis.current = false;
        reanalysisActionSheetRef.current?.close();
        handleBlockAction(statusValidation.message);
        return;
      }

      if (
        shouldNotifyProposalLoaded({
          action: ProposalValidationAction.LOAD_DRAFT,
          proposalDetails
        })
      ) {
        let resolvedFullName = pickFirstTrimmedString(
          proposalDetails.strNome,
          fullNameFromForm,
          fullNameFromProfile,
          proposalData?.strNome
        );

        if (!resolvedFullName) {
          const profileFallback = await fetchProfileNameFallback(
            cleanCPF,
            firstCardInfo
          );
          firstCardInfo = profileFallback.firstCardInfo;
          fullNameFromProfile = profileFallback.fullNameFromProfile;
          resolvedFullName = pickFirstTrimmedString(
            fullNameFromProfile,
            proposalData?.strNome
          );
        }

        const { data: clientInfo } =
          await clientResource.getClientInfo(cleanCPF);
        const proposalDataMapped =
          mapProposalToProposalDataPreservingFlowContext(proposalDetails, {
            ...proposalData,
            strReavaliacaoLimite: LimitReassessmentEnum.REANALYSIS
          });
        const additionalPhoneData =
          mapClientAdditionalPhoneToProposalData(clientInfo);

        hasLoadedDraft.current = true;
        cancelPreProposalAlterarSyncDebounce();
        setProposalData(
          {
            ...proposalDataMapped,
            ...additionalPhoneData,
            ...wayupProposalPhoneType,
            strNome: resolvedFullName || proposalDataMapped.strNome,
            strReavaliacaoLimite: LimitReassessmentEnum.REANALYSIS
          },
          { skipAlterarSync: true }
        );

        if (resolvedFullName) {
          setValue("fullName", sanitizeNameInput(resolvedFullName));
        }
        reanalysisActionSheetRef.current?.close();
        success(
          INFO_SECURITY_FEEDBACK_TOASTS.proposalLoaded.title,
          INFO_SECURITY_FEEDBACK_TOASTS.proposalLoaded.message
        );
        return;
      }

      const { data: clientInfo } = await clientResource.getClientInfo(cleanCPF);
      const profileFallback = await fetchProfileNameFallback(
        cleanCPF,
        firstCardInfo
      );
      firstCardInfo = profileFallback.firstCardInfo;
      fullNameFromProfile = profileFallback.fullNameFromProfile;
      const resolvedFullName = pickFirstTrimmedString(
        fullNameFromForm,
        fullNameFromProfile,
        proposalDetails.strNome,
        clientInfo.strNome,
        proposalData?.strNome
      );

      const proposalInput = mapClientInfoToReanalysisProposalInput(
        clientInfo,
        cleanCPF,
        proposalData,
        proposalDetails,
        resolvedFullName,
        firstCardInfo
      );
      Object.assign(proposalInput, wayupProposalPhoneType);

      const { data: proposalResponse } =
        await proposalsResource.createProposal(proposalInput);

      // Do not call Proposta/Alterar immediately after Incluir; the next steps
      // will sync any changes made after the reanalysis draft is created.
      cancelPreProposalAlterarSyncDebounce();

      const proposalDataMapped = {
        ...mapClientInfoToProposalData(
          clientInfo,
          cleanCPF,
          proposalResponse.lngProposta,
          proposalDetails,
          resolvedFullName,
          firstCardInfo
        ),
        ...wayupProposalPhoneType
      };

      setProposalData(proposalDataMapped, { skipAlterarSync: true });

      reanalysisActionSheetRef.current?.close();
      success(
        INFO_SECURITY_FEEDBACK_TOASTS.reanalysisCreated.title,
        INFO_SECURITY_FEEDBACK_TOASTS.reanalysisCreated.message
      );
    } catch (err) {
      console.error("Error creating reanalysis proposal:", err);
      error("Erro ao criar proposta de reanálise. Tente novamente.");
      hasConfirmedReanalysis.current = false;
    } finally {
      setIsCreatingReanalysis(false);
    }
  };

  const handleKeepNumber = async () => {
    if (isKeepingCurrentPhone) {
      return;
    }

    setIsKeepingCurrentPhone(true);
    const latestProposalData =
      usePreProposalStore.getState().proposalData || proposalData;
    const { phoneDDD, phone } = getProposalPhone(latestProposalData);
    const formData = extractInfoSecurityFormData({
      cpf: getValues("cpf") || latestProposalData?.strCliente || "",
      fullName: getValues("fullName") || latestProposalData?.strNome || ""
    });
    const cleanCPF = formData.cleanCPF;
    const fullName = formData.fullName;

    if (!phoneDDD || !phone) {
      error("Dados do telefone incompletos. Tente cadastrar um novo número.");
      phoneUpdateActionSheetRef.current?.close();
      push(ROUTES.preProposalsPhoneNumber);
      setIsKeepingCurrentPhone(false);
      return;
    }

    if (!cleanCPF || !fullName) {
      error("Dados do cliente incompletos. Revise o CPF e o nome completo.");
      setIsKeepingCurrentPhone(false);
      return;
    }

    try {
      setProposalData(
        {
          strCliente: cleanCPF,
          strNome: fullName
        },
        { skipAlterarSync: true }
      );

      if (
        latestProposalData?.needsContextUpdate &&
        latestProposalData?.lngProposta
      ) {
        cancelPreProposalAlterarSyncDebounce();
        await proposalsResource.editProposal(
          mapToContextUpdateInput(latestProposalData, {
            cleanCPF,
            fullName
          })
        );
        setProposalData(
          { needsContextUpdate: false },
          { skipAlterarSync: true }
        );
      }

      const currentProposalData =
        usePreProposalStore.getState().proposalData || latestProposalData;

      if (!currentProposalData?.lngProposta) {
        setProposalData(forceNewBiometryPatch(), { skipAlterarSync: true });
      } else {
        try {
          const { data: needNewBiometryResponse } =
            await requisitionsResource.getNeedNewBiometry(
              currentProposalData.lngProposta
            );
          setProposalData(
            resolveNeedNewBiometryPatch(needNewBiometryResponse),
            { skipAlterarSync: true }
          );
        } catch (biometryDecisionError) {
          console.error(
            "Error checking proposal biometry validity:",
            biometryDecisionError
          );
          setProposalData(forceNewBiometryPatch(), { skipAlterarSync: true });
        }
      }

      let currentPhoneTokenStatus:
        | ProposalPhoneTokenStatusResponseDTO
        | undefined;
      if (currentProposalData?.lngProposta) {
        try {
          const { data } =
            await requisitionsResource.getProposalPhoneTokenStatus(
              currentProposalData.lngProposta
            );
          currentPhoneTokenStatus = data;
        } catch (tokenStatusError) {
          console.error(
            "Error checking proposal phone token status:",
            tokenStatusError
          );
        }
      }

      const shouldConfirmCurrentPhoneToken =
        shouldConfirmTokenWhenKeepingCurrentPhone(
          currentProposalData,
          currentPhoneTokenStatus
        );

      if (shouldConfirmCurrentPhoneToken) {
        const { data: generateTokenResponse } =
          await commonResource.generateToken({
            strDDD: phoneDDD,
            strTelefone: phone,
            strCliente: cleanCPF,
            strNome: fullName,
            strProduto: currentProposalData?.strProduto || ""
          });

        if (!generateTokenResponse.blnSms) {
          error("Erro ao gerar o token, tente novamente.");
          return;
        }

        phoneUpdateActionSheetRef.current?.close();
        push(ROUTES.preProposalsConfirmToken);
        return;
      }

      await resolveRequiredDocumentsAfterSecurity({
        beforeNavigate: () => phoneUpdateActionSheetRef.current?.close()
      });
    } catch (err) {
      console.error("Error keeping current phone:", err);
      error("Erro ao continuar com o telefone atual. Tente novamente.");
    } finally {
      setIsKeepingCurrentPhone(false);
    }
  };

  const handleChangeNumber = () => {
    phoneUpdateActionSheetRef.current?.close();
    invalidatePendingCpfValidation();
    push(ROUTES.preProposalsPhoneNumber);
  };

  const handleCancelReanalysis = () => {
    reanalysisActionSheetRef.current?.close();
    replace(ROUTES.home);
  };

  const handleDraftLoading = (
    validationResult: ProposalValidationResult,
    formData: { cleanCPF: string; fullName: string }
  ): boolean => {
    if (!validationResult.proposalDetails) {
      return true;
    }

    hasLoadedDraft.current = true;
    setIsCpfLocked(true);

    const hasLimitReassessmentDraft =
      validationResult.flowType === ProposalFlowType.REANALYSIS;
    const proposalDataMapped = mapProposalToProposalDataPreservingFlowContext(
      validationResult.proposalDetails,
      hasLimitReassessmentDraft
        ? {
            ...proposalData,
            strReavaliacaoLimite: LimitReassessmentEnum.REANALYSIS
          }
        : proposalData
    );

    setProposalData(
      {
        ...proposalDataMapped,
        ...(hasLimitReassessmentDraft && {
          strReavaliacaoLimite: LimitReassessmentEnum.REANALYSIS
        })
      },
      { skipAlterarSync: true }
    );

    setValue("cpf", formData.cleanCPF);
    setValue("fullName", sanitizeNameInput(formData.fullName));

    if (shouldNotifyProposalLoaded(validationResult)) {
      success(
        INFO_SECURITY_FEEDBACK_TOASTS.proposalLoaded.title,
        INFO_SECURITY_FEEDBACK_TOASTS.proposalLoaded.message
      );
    }

    return true;
  };

  const handleValidationAction = (
    validationResult: ProposalValidationResult,
    formData: { cleanCPF: string; fullName: string }
  ): boolean => {
    switch (validationResult.action) {
      case ProposalValidationAction.BLOCK:
        handleBlockAction(validationResult.message);
        return false;
      case ProposalValidationAction.REANALYSIS:
        if (hasLimitReassessmentRef.current || hasConfirmedReanalysis.current) {
          return false;
        }
        reanalysisProposalIdRef.current = validationResult.proposalId || null;
        if (validationResult.clientName) {
          setValue("fullName", sanitizeNameInput(validationResult.clientName));
        }
        reanalysisActionSheetRef.current?.open();
        return false;
      case ProposalValidationAction.LOAD_DRAFT:
        handleDraftLoading(validationResult, formData);
        return true;
      default:
        return true;
    }
  };

  const submitInfoSecurity = async (data: InfoSecurityFormDataSchemaType) => {
    setIsSavingInfoSecurity(true);

    try {
      // When in reanalysis flow (e.g. returned from phone screen), show
      // the phone modal so user can choose to keep or change the number.
      // Update store with form data first so user's name edits are preserved
      // when they navigate to phone screen and back.
      if (isReanalysisProposal(proposalData) || proposalData?.queueEditMode) {
        const formData = extractInfoSecurityFormData(data);
        setProposalData(
          {
            strCliente: formData.cleanCPF,
            strNome: formData.fullName
          },
          { skipAlterarSync: true }
        );
        openPhoneUpdateOrGoToPhoneNumber();
        return;
      }

      const formData = extractInfoSecurityFormData(data);

      if (isSavedCPF(formData.cleanCPF, proposalData)) {
        if (proposalData?.needsContextUpdate && proposalData?.lngProposta) {
          const updateInput = mapToContextUpdateInput(proposalData, formData);
          cancelPreProposalAlterarSyncDebounce();
          await proposalsResource.editProposal(updateInput);
          setProposalData(
            { needsContextUpdate: false },
            { skipAlterarSync: true }
          );
        }

        setProposalData(
          {
            strCliente: formData.cleanCPF,
            strNome: formData.fullName
          },
          { skipAlterarSync: true }
        );

        if (shouldAskToKeepCurrentProposalPhone(proposalData)) {
          openPhoneUpdateOrGoToPhoneNumber();
          return;
        }

        push(ROUTES.preProposalsPhoneNumber);
        return;
      }

      const cached = lastProfileValidationRef.current;

      if (
        !cached ||
        cached.cleanCPF !== formData.cleanCPF ||
        cached.productCode !== selectedProductCode ||
        cached.result.error
      ) {
        error("Valide o CPF ao sair do campo antes de continuar.");
        return;
      }

      const validationResult = cached.result;

      if (validationResult.error) {
        error(validationResult.error);
        return;
      }

      const shouldContinue = handleValidationAction(validationResult, formData);

      if (!shouldContinue) {
        return;
      }

      if (shouldAskToKeepCurrentPhone(validationResult.proposalDetails)) {
        setProposalData(
          {
            strCliente: formData.cleanCPF,
            strNome: formData.fullName
          },
          { skipAlterarSync: true }
        );
        openPhoneUpdateOrGoToPhoneNumber();
        return;
      }

      if (proposalData?.needsContextUpdate && proposalData?.lngProposta) {
        const updateInput = mapToContextUpdateInput(proposalData, formData);
        cancelPreProposalAlterarSyncDebounce();
        await proposalsResource.editProposal(updateInput);
        setProposalData(
          { needsContextUpdate: false },
          { skipAlterarSync: true }
        );
      }

      setProposalData(
        {
          strCliente: formData.cleanCPF,
          strNome: formData.fullName
        },
        { skipAlterarSync: true }
      );

      push(ROUTES.preProposalsPhoneNumber);
    } catch (err) {
      lastProfileValidationRef.current = null;
      if (
        err instanceof Error &&
        err.message === CPF_VALIDATION_TIMEOUT_ERROR
      ) {
        error(
          "Tempo esgotado ao validar o CPF. Verifique sua conexão e tente novamente."
        );
      } else {
        console.error("Error validating info security:", err);
        error("Erro ao validar as informações, tente novamente.");
      }
    } finally {
      setIsSavingInfoSecurity(false);
      setIsContinuePressed(false);
    }
  };

  const handleInvalidSubmit = useCallback(() => {
    setIsContinuePressed(false);
  }, []);

  const handleOnSubmitForm = () => {
    setIsContinuePressed(true);
    void handleSubmit(submitInfoSecurity, handleInvalidSubmit)();
  };

  const handleCPFBlur = useCallback(() => {
    const currentCPF = getValues("cpf") || "";
    void handleCPFChange(currentCPF);
  }, [getValues, handleCPFChange]);

  return {
    control,
    isValid,
    isSubmitting,
    handleOnSubmitForm,
    back,
    reanalysisActionSheetRef,
    handleConfirmReanalysis,
    handleCancelReanalysis,
    phoneUpdateActionSheetRef,
    handleKeepNumber,
    handleChangeNumber,
    isValidatingCPF,
    handleCPFBlur,
    isCreatingReanalysis,
    isCpfLocked,
    isKeepingCurrentPhone,
    isProcessingInfoSecurity: resolveInfoSecurityProcessingState({
      isSubmitting,
      isContinuePressed,
      isCreatingReanalysis,
      isSavingInfoSecurity,
      isKeepingCurrentPhone
    })
  };
};
